import { hasSupabaseConfig, supabase } from "./supabaseClient.js";

export const DEFAULT_FILE_BUCKET = "framesbay-files";
export const STORAGE_PROVIDER = import.meta.env.VITE_STORAGE_PROVIDER || "supabase";

const IMAGE_FILE_TYPES = new Set([
  "attendance_selfie_in",
  "attendance_selfie_out",
  "cn_form",
  "fast_slip",
  "order_reference_photo",
  "supporting_photo",
  "old_lens_or_chashma",
  "vendor_return_form",
  "credit_note_copy",
  "order_photo",
  "prescription_photo",
  "delivery_proof",
  "product_photo",
  "staff_photo",
  "staff_document",
  "other"
]);

const APP_FILE_SAFE_COLUMNS = new Set([
  "id",
  "module",
  "record_type",
  "record_id",
  "store_id",
  "staff_id",
  "file_type",
  "storage_provider",
  "storage_bucket",
  "storage_path",
  "original_file_name",
  "mime_type",
  "original_file_size_bytes",
  "compressed_file_size_bytes",
  "image_width",
  "image_height",
  "uploaded_by",
  "uploaded_at",
  "is_deleted",
  "deleted_by",
  "deleted_at",
  "created_at",
  "updated_at"
]);

function uuid(prefix = "file") {
  return crypto.randomUUID ? crypto.randomUUID() : `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function safePathPart(value, fallback = "unknown") {
  return String(value || fallback)
    .trim()
    .replace(/[^a-zA-Z0-9-_]/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80) || fallback;
}

function dataUrlToBlob(dataUrl) {
  const [header, body] = String(dataUrl).split(",");
  const mimeType = header?.match(/data:(.*?);base64/)?.[1] || "image/jpeg";
  const binary = atob(body || "");
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type: mimeType });
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Photo could not be read."));
    reader.readAsDataURL(blob);
  });
}

function loadImageFromBlob(blob) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(blob);
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Photo could not be opened."));
    };
    image.src = url;
  });
}

function imageMimeType(preferred = "image/jpeg") {
  return preferred === "image/webp" ? "image/webp" : "image/jpeg";
}

export async function compressImageBeforeUpload(input, options = {}) {
  if (!input) throw new Error("Please select a photo.");
  const originalBlob = typeof input === "string" && input.startsWith("data:")
    ? dataUrlToBlob(input)
    : input instanceof Blob
      ? input
      : null;

  if (!originalBlob || !String(originalBlob.type || "").startsWith("image/")) {
    throw new Error("Please select an image file.");
  }

  const {
    maxWidth = 1200,
    quality = 0.66,
    mimeType = "image/jpeg",
    targetMaxBytes = 700 * 1024
  } = options;
  const image = await loadImageFromBlob(originalBlob);
  const scale = Math.min(1, maxWidth / image.width);
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { alpha: false });
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  const outputMimeType = imageMimeType(mimeType);
  let outputQuality = quality;
  let compressedBlob = await new Promise((resolve) => canvas.toBlob(resolve, outputMimeType, outputQuality));
  while (compressedBlob && compressedBlob.size > targetMaxBytes && outputQuality > 0.48) {
    outputQuality = Math.max(0.48, outputQuality - 0.08);
    compressedBlob = await new Promise((resolve) => canvas.toBlob(resolve, outputMimeType, outputQuality));
  }
  if (!compressedBlob) throw new Error("Photo could not be compressed.");

  return {
    blob: compressedBlob,
    mimeType: outputMimeType,
    width,
    height,
    originalFileSizeBytes: originalBlob.size || input.size || 0,
    compressedFileSizeBytes: compressedBlob.size,
    originalFileName: input.name || "camera-photo.jpg",
    previewUrl: URL.createObjectURL(compressedBlob)
  };
}

function moduleFolder(module) {
  const normalized = safePathPart(module, "files");
  if (normalized === "cnTracking" || normalized === "cn") return "cn";
  if (normalized === "orders" || normalized === "opticalOrders") return "orders";
  return normalized;
}

function buildStoragePath({ module, recordId, storeId, staffId, fileType }) {
  const timestamp = Date.now();
  const ext = "jpg";
  const folder = moduleFolder(module);
  const storePart = safePathPart(storeId, "company");
  const recordPart = safePathPart(recordId, "record");
  const staffPart = safePathPart(staffId, "staff");
  const typePart = safePathPart(fileType, "other");

  if (folder === "attendance") {
    const date = new Date().toISOString().slice(0, 10);
    return `framesbay/${storePart}/attendance/${staffPart}/${date}/${timestamp}-${typePart}.${ext}`;
  }
  if (folder === "staff") return `framesbay/${storePart}/staff/${staffPart}/${typePart}/${timestamp}.${ext}`;
  return `framesbay/${storePart}/${folder}/${recordPart}/${typePart}/${timestamp}.${ext}`;
}

function toSnakeRecord(record) {
  const map = {
    recordType: "record_type",
    recordId: "record_id",
    storeId: "store_id",
    staffId: "staff_id",
    fileType: "file_type",
    storageProvider: "storage_provider",
    storageBucket: "storage_bucket",
    storagePath: "storage_path",
    originalFileName: "original_file_name",
    mimeType: "mime_type",
    originalFileSizeBytes: "original_file_size_bytes",
    compressedFileSizeBytes: "compressed_file_size_bytes",
    imageWidth: "image_width",
    imageHeight: "image_height",
    uploadedBy: "uploaded_by",
    uploadedAt: "uploaded_at",
    isDeleted: "is_deleted",
    deletedBy: "deleted_by",
    deletedAt: "deleted_at"
  };
  return Object.fromEntries(
    Object.entries(record)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [map[key] || key, value])
  );
}

function buildAppFilePayload(metadata) {
  const snakeRecord = toSnakeRecord(metadata || {});
  const unknownKeys = Object.keys(snakeRecord).filter((key) => !APP_FILE_SAFE_COLUMNS.has(key));
  if (unknownKeys.length) {
    console.warn("Schema guard removed UI-only/unknown fields before saving app_files:", unknownKeys);
  }
  return Object.fromEntries(Object.entries(snakeRecord).filter(([key]) => APP_FILE_SAFE_COLUMNS.has(key)));
}

export async function saveFileMetadata(metadata) {
  if (!hasSupabaseConfig || STORAGE_PROVIDER !== "supabase") return metadata;
  const { data, error } = await supabase.from("app_files").upsert(buildAppFilePayload(metadata)).select().single();
  if (error) {
    const message = String(error.message || "").toLowerCase();
    if (message.includes("does not exist") || message.includes("schema cache") || message.includes("relation")) return metadata;
    if (
      message.includes("row-level security") ||
      message.includes("permission") ||
      message.includes("policy") ||
      message.includes("not authorized") ||
      error.code === "42501"
    ) {
      return metadata;
    }
    throw error;
  }
  return data;
}

export async function uploadCompressedImage({
  file,
  dataUrl,
  module = "files",
  recordType = "file",
  recordId = "",
  storeId = "",
  staffId = "",
  fileType = "other",
  uploadedBy = "",
  maxWidth = 1200,
  quality = 0.66,
  targetMaxBytes = 700 * 1024,
  bucket = DEFAULT_FILE_BUCKET,
  mimeType = "image/jpeg"
} = {}) {
  if (!IMAGE_FILE_TYPES.has(fileType)) fileType = "other";
  const compressed = await compressImageBeforeUpload(file || dataUrl, { maxWidth, quality, targetMaxBytes, mimeType });

  if (!hasSupabaseConfig || STORAGE_PROVIDER !== "supabase") {
    const localPreview = await blobToDataUrl(compressed.blob);
    return {
      id: uuid("file"),
      storageProvider: "local",
      storageBucket: "local",
      storagePath: localPreview,
      url: localPreview,
      metadata: {
        originalFileName: compressed.originalFileName,
        mimeType: compressed.mimeType,
        originalFileSizeBytes: compressed.originalFileSizeBytes,
        compressedFileSizeBytes: compressed.compressedFileSizeBytes,
        imageWidth: compressed.width,
        imageHeight: compressed.height
      }
    };
  }

  const id = uuid("file");
  const storagePath = buildStoragePath({ module, recordId: recordId || id, storeId, staffId, fileType });
  const { error } = await supabase.storage.from(bucket).upload(storagePath, compressed.blob, {
    contentType: compressed.mimeType,
    cacheControl: "31536000",
    upsert: false
  });
  if (error) throw error;

  const metadata = {
    id,
    module,
    recordType,
    recordId: recordId || id,
    storeId: storeId || null,
    staffId: staffId || null,
    fileType,
    storageProvider: STORAGE_PROVIDER,
    storageBucket: bucket,
    storagePath,
    originalFileName: compressed.originalFileName,
    mimeType: compressed.mimeType,
    originalFileSizeBytes: compressed.originalFileSizeBytes,
    compressedFileSizeBytes: compressed.compressedFileSizeBytes,
    imageWidth: compressed.width,
    imageHeight: compressed.height,
    uploadedBy: uploadedBy || null,
    uploadedAt: new Date().toISOString(),
    isDeleted: false
  };
  await saveFileMetadata(metadata);
  return { id, storageBucket: bucket, storagePath, path: storagePath, metadata };
}

export async function getFileUrl(fileOrPath, options = {}) {
  const storagePath = typeof fileOrPath === "string" ? fileOrPath : fileOrPath?.storagePath || fileOrPath?.path;
  const bucket = options.bucket || fileOrPath?.storageBucket || DEFAULT_FILE_BUCKET;
  if (!storagePath) return "";
  if (String(storagePath).startsWith("data:") || String(storagePath).startsWith("blob:") || String(storagePath).startsWith("http")) {
    return storagePath;
  }
  if (!hasSupabaseConfig || STORAGE_PROVIDER !== "supabase") return storagePath;
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(storagePath, options.expiresIn || 60 * 5);
  if (error) throw error;
  return data.signedUrl;
}

export async function deleteFileIfAllowed(fileOrPath, { deletedBy = "", bucket = DEFAULT_FILE_BUCKET } = {}) {
  const storagePath = typeof fileOrPath === "string" ? fileOrPath : fileOrPath?.storagePath || fileOrPath?.path;
  if (!storagePath || !hasSupabaseConfig || STORAGE_PROVIDER !== "supabase") return;
  const { error } = await supabase.storage.from(bucket).remove([storagePath]);
  if (error) throw error;
  if (fileOrPath?.id) {
    const { error } = await supabase
      .from("app_files")
      .update({ is_deleted: true, deleted_by: deletedBy || null, deleted_at: new Date().toISOString() })
      .eq("id", fileOrPath.id);
    if (error) throw error;
  }
}
